import { useMemo } from 'react';

import { Box } from '#/components/Box/Box';
import { Icon } from '#/components/Icon/Icon';
import type { IconName } from '#/components/Icon/IconName';
import { LegacyTooltip } from '#/components/LegacyTooltip/LegacyTooltip';
import { HeadingSmall } from '#/components/Text/HeadingSmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ApiOrgType } from '#/modules/listing/orgs/api/types/ApiOrgType';
import { colors } from '#/theme/theme';
import { LISTING_TYPE_HUMAN_NAME_MAP_TITLE } from '#/utils/constants/general/listingTypeHumanNameMapTitle';
import { ORG_TYPES } from '#/utils/constants/general/orgTypes';

import { getListingTypeLabelIcon } from './getListingTypeLabelIcon';

type Props = {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT' | 'IMPORTED' | 'ORG';
  orgType: ApiOrgType | null;
};

export function ListingTypeLabel({ listingType, orgType }: Props) {
  const { i18n } = useI18n();

  const icon = getListingTypeLabelIcon({
    listingType,
  });

  const orgTypeIcon: IconName | undefined = useMemo(() => {
    if (!orgType) return undefined;

    switch (orgType) {
      case ApiOrgType.NONPROFIT:
        return 'hand';
      case ApiOrgType.CONSULTANT:
        return 'percentage-award';
      case ApiOrgType.RECRUITER:
        return 'person-add';
      case ApiOrgType.CORPORATION:
        return 'mountain-flag';
      case ApiOrgType.GOVERNMENT:
        return 'government';
      case ApiOrgType.COMMUNITY:
        return 'group';
    }
  }, [orgType]);

  const orgTypeTitle = useMemo(() => {
    if (!orgType) return undefined;

    return (
      ORG_TYPES(i18n)[orgType] || LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n).ORG
    );
  }, [i18n, orgType]);

  const content = useMemo(() => {
    if (orgTypeTitle) return orgTypeTitle;

    // TODO: check if we can remove the IMPORTED listing type
    // if we can, add it to the translation list
    if (listingType === 'IMPORTED') return 'imported';

    return LISTING_TYPE_HUMAN_NAME_MAP_TITLE(i18n)[listingType];
  }, [i18n, listingType, orgTypeTitle]);

  const tooltipText = useMemo(() => {
    if (
      !orgType ||
      ![ApiOrgType.CORPORATION, ApiOrgType.COMMUNITY].includes(orgType)
    )
      return null;

    if (orgType === ApiOrgType.CORPORATION) {
      /* TRANSLATORS: Tooltip next to 'Business' label in organization type when creating new non-501c organization */
      return i18n(
        `Social enterprises and approved for-profit companies are allowed to post jobs on Idealist that adhere to our guidelines.`,
      );
    }

    if (orgType === ApiOrgType.COMMUNITY) {
      /* TRANSLATORS: Tooltip next to 'Community Group' label in organization type when creating new non-501c organization */
      return i18n(
        'Common interest and other mission-driven, noncommercial groups may now post listings that adhere to the Idealist guidelines.',
      );
    }
  }, [i18n, orgType]);

  return (
    <Box display="flex" alignItems="center">
      <Box mr="8px" color={colors.lightContentGrey} display="inline-flex">
        <Icon name={orgTypeIcon ?? icon} size={19} />
      </Box>

      <HeadingSmall qaId="listing-type" my={0} color={colors.lightContentGrey}>
        {content}
      </HeadingSmall>

      {tooltipText && (
        <Box ml="4px" mt="-1px">
          <LegacyTooltip qaId="listing-type-tooltip">
            {tooltipText}
          </LegacyTooltip>
        </Box>
      )}
    </Box>
  );
}
